/**
 * Builds search parameters from the UI filter state for API submission.
 * Shared by useSearchFilters (sidebar) and useExtendSearchFilters (extend modal).
 *
 * For searchType 'maps', map-specific filters (rating, reviews, closed places) are included.
 * For searchType 'hr', trade register filters (revenue, balance total, employees, legal form, events) are included.
 * WZ codes (industryCodes) take precedence over free-text industry terms for HR searches.
 *
 * @param {Object} filters - UI filter state
 * @param {string} filters.location - Location search term
 * @param {'place'|'state'|'plz'} filters.locationMode - Exactly one location criterion is sent: location/radius, states or PLZ
 * @param {string} filters.plzFrom - PLZ range lower bound, five digits (PLZ mode only)
 * @param {string} filters.plzTo - PLZ range upper bound, five digits (PLZ mode only)
 * @param {string} filters.plzList - Free-text PLZ list: codes, ranges, prefix wildcards (PLZ mode only)
 * @param {string[]} filters.states - German state codes (Maps + HR, state mode only)
 * @param {string} filters.country - Country filter
 * @param {number} filters.radius - Search radius in km
 * @param {number} filters.maxResults - Maximum number of results
 * @param {string} filters.excludeKeywords - Comma-separated exclusion keywords
 * @param {boolean} filters.includeClosedPlaces - Include closed places (Maps only)
 * @param {string} filters.websiteFilter - 'with' | 'without' | 'all'
 * @param {string} filters.ratingFrom - Minimum rating (Maps only)
 * @param {string} filters.ratingTo - Maximum rating (Maps only)
 * @param {string} filters.minReviewCount - Minimum review count (Maps only)
 * @param {boolean} filters.onlyNameMatches - Only places whose name contains a search term (Maps only)
 * @param {string[]} filters.industryCodes - WZ codes (HR only)
 * @param {string} filters.searchKeywords - Comma-separated search keywords (HR only)
 * @param {string[]} filters.legalForm - Legal form filter (HR only)
 * @param {string[]} filters.excludeProspectQueryIds - Prospect query IDs whose companies are skipped (HR only)
 * @param {string[]} filters.excludeContactListIds - Contact list IDs whose companies are skipped (HR only)
 * @param {string} filters.revenueFrom - Minimum revenue in million EUR (HR only)
 * @param {string} filters.revenueTo - Maximum revenue in million EUR (HR only)
 * @param {string} filters.balanceTotalFrom - Minimum balance total in million EUR (HR only)
 * @param {string} filters.balanceTotalTo - Maximum balance total in million EUR (HR only)
 * @param {string} filters.earningsFrom - Minimum earnings in million EUR (HR only)
 * @param {string} filters.earningsTo - Maximum earnings in million EUR (HR only)
 * @param {string} filters.employeesFrom - Minimum employee count (HR only)
 * @param {string} filters.employeesTo - Maximum employee count (HR only)
 * @param {boolean} filters.includeEstimated - Include estimated values (HR only)
 * @param {string} filters.eventType - Event type filter (HR only)
 * @param {string} filters.eventDateFrom - Event start date (HR only)
 * @param {string} filters.eventDateTo - Event end date (HR only)
 * @param {"maps"|"hr"} searchType - Search type
 * @param {string[]} searchTerms - Parsed industry/search terms
 * @param {{expandedCodes: string[], codePrefixes: string[]}|null} [resolvedIndustry] - Pre-resolved WZ codes for free-text terms (HR only)
 * @returns {Object} API-ready search parameters
 */
export const buildSearchParameters = (filters, searchType, searchTerms, resolvedIndustry = null) => {
	const params = {};

	// Shared fields. Keyed on locationMode alone so stale values of another mode
	// never leak into the search (backend rejects mixed location criteria).
	if (filters.locationMode === 'plz') {
		if (filters.plzFrom && filters.plzTo) {
			params.postalCodeFrom = filters.plzFrom;
			params.postalCodeTo = filters.plzTo;
		}
		if (filters.plzList?.trim()) params.postalCodes = filters.plzList.trim();
	} else if (filters.locationMode === 'state') {
		if (Array.isArray(filters.states) && filters.states.length > 0) params.states = filters.states;
	} else if (filters.location) {
		// A radius only means something around a location; without one both search
		// types ignore it, so it must not end up in the stored query input
		params.location = filters.location;
		if (filters.radius) params.radius = parseInt(filters.radius, 10);
	}
	if (filters.country) params.country = filters.country;
	params.maxResults = filters.maxResults || 30;

	const excludeKeywords = (filters.excludeKeywords || '')
		.split(',')
		.map((x) => x.trim())
		.filter(Boolean);
	if (excludeKeywords.length > 0) params.excludeKeywords = excludeKeywords;

	params.websiteFilter = filters.websiteFilter;

	if (searchType === 'maps') {
		params.searchTerms = searchTerms;
		params.language = 'de';
		params.skipClosedPlaces = !filters.includeClosedPlaces;
		if (filters.ratingFrom) params.ratingMin = parseFloat(filters.ratingFrom);
		if (filters.ratingTo) params.ratingMax = parseFloat(filters.ratingTo);
		if (filters.minReviewCount) params.minReviewCount = parseInt(filters.minReviewCount, 10);
		if (filters.onlyNameMatches) params.nameMatch = 'only_includes';
	} else {
		// HR Search:
		// Prefer explicit WZ codes from the manual picker over free-text terms.
		// Free-text terms stay in the params (query record + worker fallback) and are
		// complemented by pre-resolved codes/prefixes so the worker skips re-resolution.
		if (Array.isArray(filters.industryCodes) && filters.industryCodes.length > 0) {
			params.industryCodes = filters.industryCodes;
		} else if (searchTerms.length > 0) {
			params.industrySearchTerms = searchTerms;
			if (resolvedIndustry?.expandedCodes?.length > 0) params.industryCodes = resolvedIndustry.expandedCodes;
			if (resolvedIndustry?.codePrefixes?.length > 0) params.industryCodePrefixes = resolvedIndustry.codePrefixes;
		}

		const searchKeywords = (filters.searchKeywords || '')
			.split(',')
			.map((x) => x.trim())
			.filter(Boolean);
		if (searchKeywords.length > 0) params.searchKeywords = searchKeywords;

		if (filters.legalForm.length > 0) params.legalForm = filters.legalForm;

		if (filters.excludeProspectQueryIds?.length > 0)
			params.excludeProspectQueryIds = filters.excludeProspectQueryIds;
		if (filters.excludeContactListIds?.length > 0) params.excludeContactListIds = filters.excludeContactListIds;

		if (filters.revenueFrom) params.revenueMin = parseFloat(filters.revenueFrom) * 1_000_000;
		if (filters.revenueTo) params.revenueMax = parseFloat(filters.revenueTo) * 1_000_000;
		if (filters.balanceTotalFrom) params.balanceTotalMin = parseFloat(filters.balanceTotalFrom) * 1_000_000;
		if (filters.balanceTotalTo) params.balanceTotalMax = parseFloat(filters.balanceTotalTo) * 1_000_000;
		if (filters.earningsFrom) params.earningsMin = parseFloat(filters.earningsFrom) * 1_000_000;
		if (filters.earningsTo) params.earningsMax = parseFloat(filters.earningsTo) * 1_000_000;
		if (filters.employeesFrom) params.employeesMin = parseInt(filters.employeesFrom, 10);
		if (filters.employeesTo) params.employeesMax = parseInt(filters.employeesTo, 10);

		params.includeEstimated = filters.includeEstimated !== false;

		if (filters.eventType) params.eventType = filters.eventType;
		if (filters.eventDateFrom) params.eventDateFrom = filters.eventDateFrom;
		if (filters.eventDateTo) params.eventDateTo = filters.eventDateTo;
	}

	return params;
};
